/**
 * Discrete carat-size slider that syncs with variant option radios.
 * Commits the variant only on pointer-up to avoid mid-drag re-renders/scroll jumps.
 */
class CaratSizeSlider extends HTMLElement {
  static get observedAttributes() {
    return ['data-index', 'data-count'];
  }

  connectedCallback() {
    this.track = this.querySelector('[data-carat-track]');
    this.handle = this.querySelector('[data-carat-handle]');
    this.optionName = this.getAttribute('data-option-name');
    this.formId = this.getAttribute('data-form-id');
    this._syncFromAttributes();

    if (!this._bound) {
      this._bound = true;
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onOptionChange = this._onOptionChange.bind(this);

      this.track?.addEventListener('pointerdown', this._onPointerDown);
      this.handle?.addEventListener('keydown', this._onKeyDown);
      document.addEventListener('change', this._onOptionChange);
    }

    this._updateUI(false);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('pointerdown', this._onPointerDown);
    this.handle?.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('change', this._onOptionChange);
    this._unbindDrag();
    this._clearScrollLocks();
    this._bound = false;
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this._syncFromAttributes();
    this._updateUI(false);
  }

  _syncFromAttributes() {
    this.count = Math.max(1, parseInt(this.getAttribute('data-count') || '1', 10));
    this.index = Math.min(this.count - 1, Math.max(0, parseInt(this.getAttribute('data-index') || '0', 10)));
  }

  _inputs() {
    const formAttr = this.formId ? `[form="${CSS.escape(this.formId)}"]` : '';
    const nameAttr = this.optionName ? `[name="${CSS.escape(this.optionName)}"]` : '';
    return Array.from(document.querySelectorAll(`input[data-option-position]${formAttr}${nameAttr}`));
  }

  _updateUI() {
    const max = Math.max(1, this.count - 1);
    const percent = this.count <= 1 ? 0 : (this.index / max) * 100;
    this.style.setProperty('--carat-index', String(this.index));
    this.style.setProperty('--carat-percent', `${percent}%`);
    this.style.setProperty('--carat-count', String(this.count));

    if (this.handle) {
      this.handle.setAttribute('aria-valuenow', String(this.index));
      this.handle.setAttribute('aria-valuemin', '0');
      this.handle.setAttribute('aria-valuemax', String(max));
      const input = this._inputs()[this.index];
      const label = input?.dataset?.label || input?.nextElementSibling?.textContent?.trim() || input?.value || String(this.index);
      this.handle.setAttribute('aria-valuetext', label);
    }
  }

  _selectIndex(index, { silent, uiOnly } = {}) {
    const next = Math.max(0, Math.min(this.count - 1, index));
    if (next === this.index) {
      if (!silent && !uiOnly) this._commitSelection();
      return;
    }

    this.index = next;
    this._updateUI();

    if (silent || uiOnly) return;
    this._commitSelection();
  }

  _lockScroll() {
    this._lockedScrollY = window.scrollY;
    this._lockedScrollX = window.scrollX;

    if (this._scrollLockHandler) return;
    this._scrollLockHandler = () => {
      if (this._lockedScrollY == null) return;
      if (window.scrollY === this._lockedScrollY && window.scrollX === this._lockedScrollX) return;
      window.scrollTo(this._lockedScrollX, this._lockedScrollY);
    };
    window.addEventListener('scroll', this._scrollLockHandler, { passive: false });
  }

  _clearScrollLocks() {
    if (this._scrollLockHandler) {
      window.removeEventListener('scroll', this._scrollLockHandler);
      this._scrollLockHandler = null;
    }
    if (this._scrollUnlockTimer) {
      clearTimeout(this._scrollUnlockTimer);
      this._scrollUnlockTimer = null;
    }
    this._lockedScrollY = null;
    this._lockedScrollX = null;
  }

  _restoreScrollSoon() {
    const restore = () => {
      if (this._lockedScrollY == null) return;
      window.scrollTo(this._lockedScrollX, this._lockedScrollY);
    };

    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });

    if (this._scrollUnlockTimer) clearTimeout(this._scrollUnlockTimer);
    this._scrollUnlockTimer = setTimeout(() => {
      restore();
      this._clearScrollLocks();
    }, 350);
  }

  _commitSelection() {
    const input = this._inputs()[this.index];
    if (!input || input.disabled) return;
    if (input.checked) return;

    this._lockScroll();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this._restoreScrollSoon();
  }

  _indexFromClientX(clientX) {
    const rect = this.track.getBoundingClientRect();
    if (!rect.width || this.count <= 1) return 0;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(ratio * (this.count - 1));
  }

  _onPointerDown(event) {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    this._dragging = true;
    this._pendingIndex = this.index;
    this.track.setPointerCapture?.(event.pointerId);
    this._selectIndex(this._indexFromClientX(event.clientX), { uiOnly: true });
    this._pendingIndex = this.index;
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
  }

  _onPointerMove(event) {
    if (!this._dragging) return;
    this._selectIndex(this._indexFromClientX(event.clientX), { uiOnly: true });
    this._pendingIndex = this.index;
  }

  _onPointerUp() {
    if (!this._dragging) return;
    this._dragging = false;
    this._unbindDrag();
    if (this._pendingIndex != null) {
      this.index = this._pendingIndex;
      this._updateUI();
    }
    this._commitSelection();
  }

  _unbindDrag() {
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
  }

  _onKeyDown(event) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      this._selectIndex(this.index - 1);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      this._selectIndex(this.index + 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this._selectIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this._selectIndex(this.count - 1);
    }
  }

  _onOptionChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (this.formId && target.getAttribute('form') !== this.formId) return;
    if (this.optionName && target.name !== this.optionName) return;
    if (!target.matches('input[data-option-position]')) return;
    if (this._dragging) return;

    const inputs = this._inputs();
    const idx = inputs.indexOf(target);
    if (idx >= 0 && target.checked) {
      this._selectIndex(idx, { silent: true });
    }
  }
}

if (!customElements.get('carat-size-slider')) {
  customElements.define('carat-size-slider', CaratSizeSlider);
}
